import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LogIn, LogOut, Register, GetAuthenticatedUser, UpsertKeyValueStore } from "../../engine/baseApiClient";

const sliceName = "logon";
const statusIdle = "idle";
const statusLoggingIn = "logging_in";
const statusLoggingOut = "logging_out";
const statusRegistering = "registering";
const statusError = "error";
const statusIdlePL = "bezczynny";
const statusLoggingInPL = "logowanie";
const statusLoggingOutPL = "wylogowywanie";
const statusRegisteringPL = "rejestrowanie";
const statusErrorPL = "błąd";

// Initial logon state
const initialState = {
    id: "",
    username: "",    
    email: "",  
    displayName: "",
    guid: "",
    roles: [],
    isRegistered: false,
    isAdministrator: false,
    isAuthenticated: false,
    status: statusIdle,
    statusMessage: "",
    isInProgress: false,
    operationResult: "",
    isActive: false
};

// Selects
export const selectUsername = (state) => state.logon.username;
export const selectGuid = (state) => state.logon.guid;
export const selectRoles = (state) => state.logon.roles;
export const selectIsAuthenticated = (state) => state.logon.isAuthenticated;
export const selectStatusMessage = (state) => state.logon.statusMessage;
export const selectStatus = (state) => state.logon.status;
export const selectIsInProgress = (state) => state.logon.isInProgress;
export const selectEmail = (state) => state.logon.email;
export const selectDisplayName = (state) => state.logon.displayName;
export const selectOperationResult = (state) => state.logon.operationResult;
export const selectIsRegistered = (state) => state.logon.isRegistered;
export const selectIsAdministrator = (state) => state.logon.isAdministrator;
export const selectRequiresConfirmation = (state) => !state.logon.isActive && state.logon.roles.includes("REGISTERED_USER");
export const selectPurchasePlan = (state) => state.logon.purchasePlan;
export const selectPreferences = (state) => state.logon.preferences;
export const selectUserId = (state) => state.logon.id;

// Async tasks
export const doLogInAsync = createAsyncThunk(
    "logon/doLogInAsync",
    async (logInData) => {
      const response = await LogIn(logInData);
      if(!response.user.isAuthenticated){       
        throw response;
      }
      return response;
    }
);

export const doLogOutAsync = createAsyncThunk(
    "logon/doLogOutAsync",
    async () => {
      const response = await LogOut();
      return response;
    }
);

export const doRegisterAsync = createAsyncThunk(
    "logon/doRegister",
    async (registerData) => {
      const response = await Register(registerData);
      return response;
    }
);

export const doGetAuthenticatedUserAsync = createAsyncThunk(
    "logon/doGetAuthenticatedUserAsync",
    async () => {
      const response = await GetAuthenticatedUser();
      return response;
    }
);

export const doUpsertUserPlan = createAsyncThunk(
    "logon/doUpsertUserPlan",
    async (userPlanData) => {
      const response = await UpsertKeyValueStore(userPlanData.key, {});
      return response;
    }
);


// Create slice
export const logonSlice = createSlice({
    name: sliceName,
    initialState: initialState,
    reducers: {
        doSetLogout: (state) =>{
            state.status = statusIdle;
            state.isInProgress = false;       
            state.statusMessage = statusIdlePL;
            state.username = initialState.username;
            state.guid = initialState.guid;
            state.id = initialState.id;
            state.roles = initialState.roles;
            state.isRegistered = initialState.isRegistered;
            state.isAdministrator = initialState.isAdministrator;
            state.isAuthenticated = initialState.isAuthenticated;
            state.email = initialState.email;
            state.displayName = initialState.displayName;       
            state.isActive = initialState.isActive;       
        }
     },
    extraReducers: (builder) => {
        builder
            .addCase(doGetAuthenticatedUserAsync.fulfilled, (state, action) => {
                state.status = statusIdle;
                state.statusMessage = statusIdlePL;
                state.username = action.payload.user.username;
                state.guid = action.payload.ownerId;
                state.id = action.payload.id;
                state.roles = action.payload.user.roles;
                state.isRegistered = action.payload.user.roles.includes("REGISTERED_USER");
                state.isAdministrator = action.payload.user.roles.includes("ADMINISTRATOR");
                state.isAuthenticated = action.payload.user.isAuthenticated;
                state.email = action.payload.user.email;
                state.isInProgress = false;
                state.isActive = action.payload.user.isActive;
                state.preferences = action.payload.user.preferences;
                if(state.isRegistered && action.payload.user.username?.length > 0) state.displayName = action.payload.user.username;
                else state.displayName = action.payload.user.email;
            })
            .addCase(doLogInAsync.pending, (state) => {
                state.status = statusLoggingIn;    
                state.isInProgress = true;       
            })
            .addCase(doLogInAsync.fulfilled, (state, action) => {
                state.status = statusIdle;
                state.isInProgress = false;       
                state.statusMessage = statusIdlePL;
                // Authenticated user
                console.log("Login successful");
                state.username = action.payload.user.username;
                state.guid = action.payload.ownerId;
                state.id = action.payload.id;
                state.roles = action.payload.user.roles;
                state.isRegistered = action.payload.user.roles.includes("REGISTERED_USER");
                state.isAdministrator = action.payload.user.roles.includes("ADMINISTRATOR");
                state.isAuthenticated = action.payload.user.isAuthenticated; 
                state.email = action.payload.user.email;
                state.isActive = action.payload.user.isActive;
                if(state.isRegistered && action.payload.user.username?.length > 0) state.displayName = action.payload.user.username;
                else state.displayName = action.payload.user.email;
             })
            .addCase(doLogInAsync.rejected, (state, action) => {
                state.status = statusError;
                state.isInProgress = false;       
                state.statusMessage = statusErrorPL;
                state.operationResult = statusErrorPL;
                console.log(action.error);
                console.log("Login failure");
            })
            .addCase(doLogOutAsync.pending, (state) => {
                state.status = statusLoggingOut;
                state.isInProgress = true;       
                state.statusMessage = statusLoggingOutPL;
            })
            .addCase(doLogOutAsync.fulfilled, (state) => {                
                state.status = statusIdle;
                state.isInProgress = false;       
                state.statusMessage = statusIdlePL;
                state.username = initialState.username;
                state.guid = initialState.guid;
                state.id = initialState.id;
                state.roles = initialState.roles;
                state.isRegistered = initialState.isRegistered;
                state.isAdministrator = initialState.isAdministrator;
                state.isAuthenticated = initialState.isAuthenticated;
                state.email = initialState.email;
                state.displayName = initialState.displayName;       
                state.isActive = initialState.isActive;       
                console.log("Logout successful");
            })
            .addCase(doLogOutAsync.rejected, (state, action) => {
                state.status = statusError;
                state.statusMessage = statusErrorPL;
                console.log(action.error);
                console.log("Logout failure");
            });
           
    },
});

export const { doSetLogout } = logonSlice.actions;
export default logonSlice.reducer;
